import { cn } from '@/lib/utils';
import { useChartTheme } from './charts/chartTheme';
import { formatCurrencyUsd } from '@/components/organisms/org-admin/usageFormat';
import { NEAR_LIMIT_UTILISATION } from '@/services/org-admin/usageThresholds';

/**
 * A seat's (or an org's) spend against its budget, as a proportion.
 *
 * Extracted from `SpendSummaryPanel`, which had the only implementation, so the per-member table
 * could show the same thing without a second one drifting away from it. The colour thresholds and
 * the accessible-label wording are the parts that must not diverge: two meters that disagree about
 * when amber starts are worse than one meter.
 *
 * ## Why a meter and not two numbers
 *
 * The table used to print `$190.00 / $240.00` and leave the reader to divide. Budget standing is a
 * PROPORTION — the question is "how close to the limit", not "what are the two operands" — so the
 * proportion gets the visual encoding and the numbers stay alongside for precision.
 *
 * ## Colour
 *
 * Amber only in the last stretch before the limit, so it is an early warning rather than crying
 * wolf at 50%; red once passed, where the fill is pinned full. These are STATUS colours and mean
 * exactly what they say — `chartTheme` reserves `overage` for this and nothing else.
 */

export interface BudgetMeterProps {
  /** Spend on the clock the budget is enforced against — the CYCLE figure, never the window. */
  spentUsd: number;
  budgetUsd: number;
  /** `sm` is the in-table variant: shorter track, no surrounding text. */
  size?: 'sm' | 'md';
  /** Names the subject in the accessible label, e.g. an email. */
  label: string;
  className?: string;
}

export function BudgetMeter({
  spentUsd,
  budgetUsd,
  size = 'md',
  label,
  className,
}: BudgetMeterProps) {
  const theme = useChartTheme();

  /*
   * No budget means no meter, and deliberately not a full or empty one: an unlimited seat has no
   * proportion to show, and rendering an empty track would read as "0% used" — which is a claim
   * about spending rather than about the absence of a ceiling.
   */
  if (!budgetUsd || budgetUsd <= 0) {
    return <span className={cn('oa-meta', className)}>No limit</span>;
  }

  const utilisation = spentUsd / budgetUsd;
  const isOver = utilisation >= 1;
  const isNearLimit = !isOver && utilisation >= NEAR_LIMIT_UTILISATION;
  const percent = Math.round(utilisation * 100);

  return (
    <div
      className={cn('overflow-hidden rounded-full bg-muted', size === 'sm' ? 'h-1.5' : 'h-2', className)}
      role="meter"
      aria-valuenow={percent}
      aria-valuemin={0}
      /*
       * 100, because the track IS the budget. A screen reader hearing "140 of 100" learns the same
       * thing a sighted reader gets from a full bar plus the overage figure beside it.
       */
      aria-valuemax={100}
      aria-label={
        isOver
          ? `${label}: budget exceeded, ${percent}% of ${formatCurrencyUsd(budgetUsd)}, ${formatCurrencyUsd(spentUsd - budgetUsd)} over`
          : `${label}: ${percent}% of ${formatCurrencyUsd(budgetUsd)} budget used`
      }
    >
      <div
        className="h-full transition-[width] duration-300 motion-reduce:transition-none"
        style={{
          width: `${Math.min(100, utilisation * 100)}%`,
          backgroundColor: isOver
            ? theme.overage
            : isNearLimit
              ? 'hsl(var(--warning))'
              : theme.magnitude,
        }}
      />
    </div>
  );
}

/**
 * A proportional bar sat behind a right-aligned number in a table cell.
 *
 * The tables printed magnitudes as bare digits, so comparing two rows meant reading numerals and
 * doing the ratio mentally — "is 1,060,000 twice 530,000 or ten times?". The number stays (it is
 * the precise answer); the bar adds the comparison the column was missing.
 *
 * Deliberately a wash rather than a solid fill: this is a background cue behind text, and a
 * saturated block behind a numeral hurts contrast and shouts louder than the value it describes.
 */
export function CellBar({
  value,
  max,
  children,
}: {
  value: number;
  max: number;
  children: React.ReactNode;
}) {
  const share = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;

  return (
    <span className="relative flex items-center justify-end">
      <span
        aria-hidden
        className="absolute inset-y-0 right-0 rounded-sm bg-primary/[0.10]"
        style={{ width: `${share * 100}%` }}
      />
      <span className="relative">{children}</span>
    </span>
  );
}
